<script setup lang="ts">
  import { ref, onBeforeMount, computed, onBeforeUnmount } from 'vue'
  import { CometChat } from '@cometchat-pro/chat'

  import SvgIcon from '@shared/components/svg-icon.vue'
  import CImage from '@shared/components/ui/c-image.vue'
  import UserIcon from '@shared/components/user-icon.vue'
  import NavList from '@internal/components/navigation-list/navigation-list.vue'
  import SearchBar from '@internal/components/common/search-bar.vue'

  import type { INavigationList } from '@internal/types/navigation-list'
  import type IOrganization from '@/shared/api/user-management/resources/organization/organization'
  import type IUser from '@shared/api/user-management/resources/user/user'
  import type INotification from '@shared/api/patients/resources/notifications/notification'
  import type IPatient from '@shared/api/patients/resources/patients/patient'
  import type ICommunity from '@shared/api/user-management/resources/community-controller/community'
  import { ERouterList } from '@internal/router/constants/router-list'
  import { EUserRole } from '@shared/api/user-management/resources/user/constants/user-roles'

  import { getUser } from '@shared/api/user-management/user'
  import { getNotificationsList } from '@shared/api/patients/notifications'
  import { searchPatient } from '@shared/api/patients/patients'
  import { getOrganizationList } from '@shared/api/user-management/organization-controller'
  import { getCommunity } from '@shared/api/user-management/community-controller'

  import authorization from '@shared/utils/authorization'
  import { useResizeHandler } from '@shared/utils/composables/resize-handler'
  import { checkForUnreadMessages } from '@/shared/api/messaging/messaging'
import { usePostHog } from '@/shared/utils/composables/posthog'

  const { windowHeight, mdAndDown, mdAndUp } = useResizeHandler()

	const posthog = usePostHog()
  const isLoading = ref<boolean>(false)
  const user = ref<IUser | null>(null)
  const navigationListRef = ref<HTMLCollection>()

  const notificationList = ref<INotification[]>([])
  const searchResult = ref<IPatient[]>([])
  const organizationList = ref<IOrganization[]>([])
  const location = ref<ICommunity>()
  const intervalId = ref<NodeJS.Timeout | null>(null)

  onBeforeMount(async () => {
    isLoading.value = true
    try {
      await checkForUnreadMessages(authorization.userId)
      intervalId.value = setInterval(async () => {
        await checkForUnreadMessages(authorization.userId)
      }, 60000)
      if (authorization.hasAccess([EUserRole.COMMUNITY_ADMIN])) {
        const locationIdList = Object.keys(authorization.userLocationList)
        location.value = await getCommunity({ communityId: locationIdList[0] })
      }
      ;[user.value, organizationList.value] = await Promise.all([
        getUser({ userId: authorization.userId }),
        getOrganizationList(),
      ])
      if (authorization.hasAccess([EUserRole.PRACTICE_ADMIN, EUserRole.PRACTICE_CLINICIAN])) {
        notificationList.value = await getNotificationsList({ unResolved: true })
      }
    } finally {
      isLoading.value = false
    }
  })

  onBeforeUnmount(() => {
    if (intervalId.value) {
      clearInterval(intervalId.value)
    }
  })

  const notificationIcon = computed(() => {
    return notificationList.value.length ? 'notification-unresolved' : 'notification-resolved'
  })

  const locationRoute = computed(() => {
    if (authorization.hasAccess([EUserRole.PRACTICE_ADMIN, EUserRole.PRACTICE_CLINICIAN])) {
      return { route: ERouterList.ORGANIZATIONS }
    }
    if (authorization.hasAccess([EUserRole.ORGANIZATION_ADMIN])) {
      const formattedOrganizationList = Object.keys(authorization.userOrganizationList)
      return formattedOrganizationList.length === 1
        ? {
            route: ERouterList.ORGANIZATION,
            routeParams: {
              id: formattedOrganizationList[0],
            },
          }
        : {
            route: ERouterList.ORGANIZATIONS,
          }
    }
    if (authorization.hasAccess([EUserRole.COMMUNITY_ADMIN]) && location.value) {
      return {
        route: ERouterList.ORGANIZATION,
        routeParams: {
          id: location.value.organizationId,
        },
      }
    }
    return null
  })

  const isOpenMobileMenu = ref<boolean>(false)
  const isDevelopment = ref<boolean>(import.meta.env.TEMBO_IS_DEVELOPMENT === 'true')
  const navigationList = computed((): INavigationList[] => {
    let navList = [
      {
        name: 'Tasks',
        icon: 'tasks',
        route: ERouterList.TASKS,
        hasAccess: true,
      },
      {
        name: 'Alerts',
        icon: 'splash',
        route: ERouterList.ALERTS,
        hasAccess: true,
      },
      {
        name: 'Visits',
        icon: 'visits',
        route: ERouterList.VISITS,
        hasAccess: true,
      },
      {
        name: 'Patients',
        icon: 'user-management',
        route: ERouterList.PATIENTS,
        hasAccess: true,
      },
      {
        name: 'Locations',
        icon: 'dashboard',
        route: ERouterList.ORGANIZATIONS,
        ...locationRoute.value,
        hasAccess: authorization.hasAccess([
          EUserRole.PRACTICE_ADMIN,
          EUserRole.PRACTICE_CLINICIAN,
          EUserRole.ORGANIZATION_ADMIN,
          EUserRole.COMMUNITY_ADMIN,
        ]),
      },
      {
        name: 'Analytics',
        icon: 'analytics',
        route: ERouterList.ANALYTICS,
        hasAccess: true,
      },
      {
        name: 'Tembo Users',
        icon: 'tembo-users',
        route: ERouterList.TEMBO_USERS,
        hasAccess: authorization.hasAccess([EUserRole.PRACTICE_ADMIN]),
      },
      {
        name: 'DCM',
        icon: 'conditions',
        route: ERouterList.DCM,
        hasAccess: authorization.hasAccess([EUserRole.PRACTICE_ADMIN, EUserRole.PRACTICE_CLINICIAN]),
      },

      {
        name: 'Notifications',
        icon: notificationIcon.value,
        route: ERouterList.NOTIFICATIONS,
        hasAccess: authorization.hasAccess([EUserRole.PRACTICE_ADMIN, EUserRole.PRACTICE_CLINICIAN]),
      },
    ]

    if (import.meta.env.TEMBO_ENABLE_MESSAGING === 'true') {
      navList.splice(navList.length - 1, 0, {
        name: 'Messaging',
        icon: 'message',
        route: ERouterList.MESSAGING,
        hasAccess: authorization.hasAccess([EUserRole.PRACTICE_ADMIN, EUserRole.PRACTICE_CLINICIAN]),
      })
    }

    if (!mdAndUp.value) {
      navList = [
        {
          name: 'Search',
          icon: 'search',
          route: ERouterList.SEARCH,

          hasAccess: true,
        },
        ...navList,
      ]
    }
    return navList.filter(el => el.hasAccess)
  })

  const userFullName = computed(() => {
    if (user.value) {
      return `${user.value.firstName} ${user.value.lastName}`
    }
    return ''
  })

  const userEmail = computed(() => {
    return user.value ? user.value.email : ''
  })

  const handleLogout = async () => {
    authorization.logout(posthog)
    await CometChat.logout()
  }

  const isOpenSearchBar = ref<boolean>(false)
  const toggleMobileMenu = () => {
    if (mdAndUp.value && isOpenSearchBar.value) {
      toggleSearchBar()
      return
    }
    isOpenMobileMenu.value = !isOpenMobileMenu.value
  }

  const containterHeight = computed(() => {
    return mdAndDown.value ? windowHeight.value - 60 : windowHeight.value
  })

  const activeTabElement = ref<Element>()

  const toggleSearchBar = () => {
    isOpenSearchBar.value = !isOpenSearchBar.value
    if (isOpenSearchBar.value && mdAndUp.value) {
      activeTabElement.value = document.getElementsByClassName(
        'navigation-list-item router-link-exact-active router-link-active'
      )[0]
      activeTabElement.value?.classList?.remove('router-link-exact-active', 'router-link-active')
      return
    }
    activeTabElement.value?.classList.add('router-link-exact-active', 'router-link-active')
  }

  const isLoadingSearch = ref<boolean>(false)

  const handleSearchInput = async (value: string) => {
    try {
      searchResult.value = await searchPatient({ name: value.toLocaleLowerCase() })
    } finally {
      isLoadingSearch.value = false
    }
  }

  const setLoadingSearch = (value: boolean) => {
    isLoadingSearch.value = value
  }

  const isShowBlur = computed(() => {
    return (isOpenMobileMenu.value && !mdAndUp.value) || isOpenSearchBar.value
  })
</script>

<template>
  <el-container class="tw-relative tw-bg-grey-300" :style="{ height: `${windowHeight}px` }">
    <el-header
      class="mobile-header tw-flex tw-items-center md:tw-hidden tw-bg-primary-100"
      :class="{ 'tw-bg-accent-100': isDevelopment }"
    >
      <div
        class="tw-flex tw-justify-center tw-items-center tw-w-48 tw-h-48 tw-bg-primary-200 tw-rounded-4 tw-cursor-pointer"
        @click="toggleMobileMenu"
      >
        <svg-icon name="hamburger-menu" class="tw-fill-white" size="32" />
      </div>
      <div class="tw-mx-auto">
        <c-image class="tw-w-full" name="logo-text" />
      </div>
    </el-header>

    <el-container>
      <el-aside
        class="tw-z-20 tw-absolute tw-transition tw-duration-200 tw-ease-in-out tw-top-0 -tw-translate-x-full md:tw-translate-x-0 md:tw-static tw-flex tw-flex-col tw-bg-primary-200"
        :class="{ 'is-open-mobile-menu': isOpenMobileMenu }"
        :style="{ height: `${windowHeight}px` }"
        width="245px"
      >
        <header
          class="tw-hidden tw-p-24 tw-bg-primary-100 md:tw-flex tw-justify-center tw-items-center"
          :class="{ 'tw-bg-accent-100': isDevelopment }"
        >
          <router-link to="/">
            <c-image name="logo-text" />
          </router-link>
        </header>
        <nav>
          <div
            v-if="mdAndUp"
            class="search-list-item tw-my-6 tw-relative tw-flex tw-items-center tw-mx-10 tw-p-10 tw-text-grey-500 tw-rounded-4 tw-bg-transparent tw-transition-colors tw-cursor-pointer"
            :class="{ 'router-link-active router-link-exact-active': isOpenSearchBar }"
            @click="toggleSearchBar"
          >
            <svg-icon name="search" class="tw-fill-grey-500 tw-mr-10" />
            <span class="tw-text-subtitle-1 tw-text-grey-500">Search</span>
          </div>
          <nav-list ref="navigationListRef" :list="navigationList" @change-route="toggleMobileMenu" />
        </nav>
        <div class="tw-mt-auto tw-py-26 tw-mx-10">
          <router-link
            class="tw-flex tw-items-center tw-pl-8 tw-pr-16"
            :title="userEmail"
            :to="{ name: ERouterList.SETTINGS }"
            @click="toggleMobileMenu"
          >
            <template v-if="user">
              <user-icon
                :first-name="user.firstName"
                :last-name="user.lastName"
                :string-color="user.userId"
                not-active-color="#D0DAE3"
                :is-active="true"
                font-bold
                class="tw-flex-shrink-0 tw-mr-12"
              />
              <div class="tw-flex tw-flex-col tw-gap-4 tw-max-w-[130px]">
                <span
                  class="tw-text-subtitle-1 tw-text-grey-100 tw-capitalize tw-text-ellipsis tw-overflow-hidden tw-whitespace-nowrap"
                >
                  {{ userFullName }}
                </span>
                <div
                  class="tw-text-subtitle-1 tw-text-grey-100 tw-text-ellipsis tw-overflow-hidden tw-whitespace-nowrap"
                >
                  {{ userEmail }}
                </div>
              </div>
            </template>
            <el-skeleton v-else animated>
              <template #template>
                <div class="tw-flex tw-items-center">
                  <el-skeleton-item variant="image" class="tw-min-w-[36px] tw-min-h-[36px] tw-mr-12" />
                  <div class="tw-flex tw-flex-col tw-w-full tw-gap-4">
                    <el-skeleton-item variant="p" />
                    <el-skeleton-item variant="p" />
                  </div>
                </div>
              </template>
            </el-skeleton>
            <svg-icon class="tw-ml-auto tw-fill-white" name="angle-arrow" />
          </router-link>
          <div class="tw-my-24 tw-w-full tw-h-1 tw-bg-grey-110"></div>
          <div
            class="tw-flex tw-items-center tw-px-8 tw-cursor-pointer tw-transition-colors tw-text-grey-500 hover:tw-text-white"
            @click="handleLogout"
          >
            <svg-icon name="log-out" class="tw-fill-grey-500" />
            <span class="tw-ml-10 tw-text-subtitle-1">Logout</span>
          </div>
        </div>
      </el-aside>
      <transition name="slide-fade" class="tw-absolute tw-left-[245px]">
        <search-bar
          v-if="!mdAndDown && isOpenSearchBar"
          :is-open-mobile-menu="isOpenSearchBar"
          :patients-list="searchResult"
          :is-loading="isLoadingSearch"
          :organization-list="organizationList"
          :style="{ height: `${windowHeight}px` }"
          @handle-search-input="handleSearchInput"
          @close="toggleSearchBar"
          @set-loading="setLoadingSearch"
        />
      </transition>

      <el-main class="inner-layout__main" :style="{ height: `${containterHeight}px` }">
        <slot />
      </el-main>
    </el-container>

    <div
      v-if="isShowBlur"
      class="tw-z-10 tw-absolute tw-top-0 tw-left-0 tw-w-[100vw] tw-h-[100vh] tw-bg-grey-820"
      @click="toggleMobileMenu"
    />
  </el-container>
</template>

<style scoped lang="scss">
  .inner-layout {
    &__main {
      overflow-anchor: none;
    }
  }
  .el-main {
    @apply tw-p-0 tw-bg-grey-300 tw-h-screen;
  }
  .mobile-header {
    @apply tw-py-8 tw-px-12;
  }
  .is-open-mobile-menu {
    @apply tw-translate-x-0;
  }
  .search-list-item {
    $this: &;

    &::before {
      content: '';
      @apply tw-absolute -tw-left-10 tw-block tw-w-2 tw-h-full tw-bg-transparent tw-rounded-2 tw-transition-colors;
    }

    &:hover,
    &.router-link-exact-active {
      @apply tw-text-white tw-bg-grey-110;

      &::before {
        @apply tw-bg-white;
      }
    }
  }

  .slide-fade-enter-active,
  .slide-fade-leave-active {
    @apply tw-transition tw-duration-200 tw-ease-in-out;
  }

  .slide-fade-enter-from,
  .slide-fade-leave-to {
    @apply tw-translate-x-[-450px];
  }
</style>
